import React from 'react';
import {
  Tooltip,
  CircularProgress,
  IconButton,
  makeStyles,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  DefaultButton,
  PrimaryButton,
} from '@bb-ui/react-library';
import { Stop } from '@bb-ui/icons/dist/small/Stop';
import { apiUrl } from 'utils/apiUrl';
import useRestApi from 'hooks/useRestApi';
import { JobExecution, ListExecutionsResponseBody } from '@lct-jobs/core';
import { useSnackbar } from '../../../../hooks/useSnackbar';
import { useTranslation } from 'react-i18next';

interface CancelSiteExecutionControlsProps {
  tenantId: string;
  isLoading?: boolean;
  isCancellable: boolean;
}

interface MigrationSetupPhaseInput {
  sourceLearnInstanceTenantId: string;
  CertificateARN?: string;
  targetAccount: string;
  targetFleetId: string;
  targetRegion: string;
  targetTimezone: string;
}

interface CreateLearnInstanceOutput {
  AccountId: string;
  Band: string;
  ClientId: string;
  Deploy: boolean;
  FleetId: string;
  InstallationId: string;
  IsSharedRds: boolean;
  LearnSiteTenantId: string;
  LearnVersion: string;
  LicenseInfo: string;
  Name: string;
  Region: string;
  SalesforceLicense: string;
  SalesforceLicenseId: string;
  SharedRdsId: string;
  Status: string;
  Subdomain: string;
  TenantId: string;
  Timezone: string;
  Url: string;
  created: number;
  updated: number;
}

interface MigrationJobExecution extends JobExecution {
  jobInput: MigrationSetupPhaseInput;
}

interface CreateLearnInstanceExecution extends JobExecution {
  jobOutput: CreateLearnInstanceOutput;
}

const useStyles = makeStyles(() => ({
  cancelButton: {
    transition: 'all 0.2s ease',
    '&:disabled': {
      opacity: 0.6,
    },
  },
}));

const CancelSiteExecutionControls: React.FC<CancelSiteExecutionControlsProps> = ({
  tenantId,
  isLoading = false,
  isCancellable,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const executionDataRef = React.useRef(false);
  const [formDisabled, setFormDisabled] = React.useState(false);

  const jobExecutionsApi = useRestApi<ListExecutionsResponseBody>(
    apiUrl('lct', `tenants/${tenantId}/jobManager/executions`),
    { manual: true },
  );

  const runJobApi = useRestApi<JobExecution>(apiUrl('lct', 'tenants'), { manual: true });

  const cancelJobApi = useRestApi<void>(
    apiUrl('lct', `tenants/${tenantId}/jobManager/executions`),
    { manual: true },
  );

  const tenantStateApi = useRestApi(apiUrl('tenancy', `tenants`), { manual: true });

  const handleOpenDialog = async () => {
    setDialogOpen(true);
    await jobExecutionsApi.fetch();
    executionDataRef.current = true;
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    executionDataRef.current = false;
  };

  const handleConfirmCancel = () => {
    if (!executionDataRef.current && !jobExecutionsApi.data) {
      enqueueSnackbar('No execution data available', { variant: 'error' });
      return;
    }

    const presyncJob = jobExecutionsApi.data?.executions.find(
      (execution: JobExecution) => execution.jobId === 'MigrationSetupPhase',
    ) as MigrationJobExecution | undefined;

    const createLearnInstanceJob = jobExecutionsApi.data?.executions.find(
      (execution: JobExecution) => execution.jobId === 'CreateLearnInstance',
    ) as CreateLearnInstanceExecution | undefined;

    const presyncStatus = presyncJob
      ? presyncJob.status === 'IN_PROGRESS' || presyncJob.status === 'COMPLETED'
      : false;

    if (presyncJob && presyncStatus && createLearnInstanceJob?.jobOutput?.TenantId) {
      tenantStateApi.doPatch(`${createLearnInstanceJob.jobOutput.TenantId}/state`, {
        state: 'EXECUTING_MIGRATION_ROLLBACK',
        locked: false,
        forced: true,
      });
      runJobApi.doPost(
        {
          jobId: 'MigrationRollback',
          jobInput: {
            sourceLearnInstanceTenantId: presyncJob.jobInput.sourceLearnInstanceTenantId,
            deleteStacks: true,
          },
        },
        undefined,
        `${createLearnInstanceJob.jobOutput.TenantId}/jobManager/executions`,
      );
    } else {
      enqueueSnackbar('No active job execution found to cancel', { variant: 'warning' });
      executionDataRef.current = false;
      setFormDisabled(false);
      setDialogOpen(false);
    }
  };

  React.useEffect(() => {
    if (runJobApi.succeededRequests.length > 0) {
      enqueueSnackbar('Site execution cancelled and migration rollback initiated', {
        variant: 'success',
      });
      executionDataRef.current = false;
      setDialogOpen(false);
    }
  }, [runJobApi.succeededRequests.length, enqueueSnackbar]);

  React.useEffect(() => {
    if (runJobApi.failedRequests.length > 0) {
      enqueueSnackbar('Failed to cancel single site execution & initiate rollback', {
        variant: 'error',
      });
      executionDataRef.current = false;
      setFormDisabled(false);
      setDialogOpen(false);
    }
  }, [runJobApi.failedRequests.length, enqueueSnackbar]);

  React.useEffect(() => {
    if (cancelJobApi.succeededRequests.length > 0) {
      enqueueSnackbar('Site execution cancelled successfully', { variant: 'success' });
      executionDataRef.current = false;
      setDialogOpen(false);
    }
  }, [cancelJobApi.succeededRequests.length, enqueueSnackbar]);

  React.useEffect(() => {
    if (cancelJobApi.failedRequests.length > 0) {
      enqueueSnackbar('Failed to cancel site execution', { variant: 'error' });
      executionDataRef.current = false;
      setFormDisabled(false);
      setDialogOpen(false);
    }
  }, [cancelJobApi.failedRequests.length, enqueueSnackbar]);

  React.useEffect(() => {
    if (jobExecutionsApi.error && dialogOpen) {
      enqueueSnackbar('Failed to fetch job executions', { variant: 'error' });
      setDialogOpen(false);
      executionDataRef.current = false;
    }
  }, [jobExecutionsApi.error, dialogOpen, enqueueSnackbar]);

  if (isLoading) {
    return <CircularProgress size={16} id={`badge-spinner-${tenantId}`} label="loading" />;
  }

  return (
    <>
      <Tooltip title="Cancel Site Upgrade Execution">
        <span>
          <IconButton
            aria-label="Cancel site execution"
            size="small"
            className={classes.cancelButton}
            disabled={!isCancellable || jobExecutionsApi.loading}
            onClick={handleOpenDialog}
          >
            <Stop />
          </IconButton>
        </span>
      </Tooltip>

      <Dialog open={dialogOpen} onClose={handleCloseDialog}>
        <DialogTitle onClose={handleCloseDialog} closeLabel={t('global.close')}>
          {t('cohorts.cancelSiteExecution')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('cohorts.cancelExecutionDialogConfirmationMessage')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <DefaultButton
            onClick={() => {
              setDialogOpen(false);
              handleCloseDialog();
            }}
            disabled={jobExecutionsApi.loading}
          >
            {t('global.cancel')}
          </DefaultButton>
          <PrimaryButton
            onClick={() => {
              handleConfirmCancel();
              setFormDisabled(true);
            }}
            disabled={jobExecutionsApi.loading || formDisabled}
          >
            {jobExecutionsApi.loading || formDisabled ? (
              <>{t('global.loadingIndicator.loading')}</>
            ) : (
              t('cohorts.cancelExecutionDialog.confirm')
            )}
          </PrimaryButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CancelSiteExecutionControls;
